import type { StoreAdmin, StoreRole } from "@hobbyshopify/backend-client";
import { type FormEvent, useId } from "react";
import { useObserver, useSubtree } from "subtree.js/react";
import { roleLabel } from "../../../../core/primitives/store-role";
import { type AdminMember, ShopAdminsSectionActions, ShopAdminsSectionState } from "./shop-admins-section-model";

function removeConfirmText(admin: StoreAdmin): string {
  return admin.name ? `Remove ${admin.name} (${admin.email}) from this shop?` : `Remove ${admin.email} from this shop?`;
}

function AdminRow({
  member,
  assignableRoles,
  changing,
  removing,
  onChangeRole,
  onRemove,
}: {
  member: AdminMember;
  assignableRoles: readonly StoreRole[];
  changing: boolean;
  removing: boolean;
  onChangeRole: (role: StoreRole) => void;
  onRemove: () => void;
}) {
  const { admin } = member;
  return (
    <li className="flex items-center gap-3 rounded-row border border-line px-3.5 py-3">
      <div className="min-w-0 flex-1">
        <p className="text-[13px] font-medium text-white">
          {admin.name}
          {admin.isSelf && <span className="ml-2 text-xs font-normal text-muted">You</span>}
        </p>
        <p className="truncate text-xs leading-snug text-muted">{admin.email}</p>
      </div>
      <span className="shrink-0 rounded-full bg-surface-strong px-2 py-0.5 text-xs text-muted">
        {roleLabel(admin.role)}
      </span>
      {member.canChangeRole && (
        <select
          value={admin.role}
          disabled={changing}
          onChange={(event) => onChangeRole(event.target.value as StoreRole)}
          className="field btn-sm shrink-0"
        >
          {assignableRoles.map((role) => (
            <option key={role} value={role}>
              {roleLabel(role)}
            </option>
          ))}
        </select>
      )}
      {member.canRemove && (
        <button type="button" onClick={onRemove} disabled={removing} className="btn-ghost shrink-0">
          {removing ? "Removing…" : "Remove"}
        </button>
      )}
    </li>
  );
}

/**
 * Who runs this shop, and at what role. Owners can do everything; Managers add
 * and remove Staff and run day to day; Staff scan, sell and take payments.
 * Adding is by email and the account has to exist first.
 */
export function ShopAdminsSection() {
  const state = useSubtree(ShopAdminsSectionState);
  const actions = useSubtree(ShopAdminsSectionActions);
  const observer = useObserver();
  const members = observer.watch(state.members);
  const status = observer.watch(state.status);
  const email = observer.watch(state.email);
  const newRole = observer.watch(state.newRole);
  const assignableRoles = observer.watch(state.assignableRoles);
  const adding = observer.watch(state.adding);
  const changingId = observer.watch(state.changingId);
  const removingId = observer.watch(state.removingId);
  const error = observer.watch(state.error);
  const added = observer.watch(state.added);
  const emailId = useId();
  const roleId = useId();

  const showRolePicker = assignableRoles.length > 1;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    void actions.add();
  };

  const remove = (member: AdminMember) => {
    if (!window.confirm(removeConfirmText(member.admin))) return;
    void actions.remove(member.admin);
  };

  return (
    <section className="panel p-6 sm:p-8">
      <h2 className="text-base font-semibold text-white">Team</h2>
      <p className="mt-1 text-sm text-muted">
        Owners can do everything, including payouts and shop settings. Managers can edit inventory, host Live shows and
        add or remove Staff. Staff can review scans, sell and take payments. Everyone needs a HobbyScan account of their
        own first.
      </p>

      {error && <p className="note note-danger mt-4 text-danger">{error}</p>}
      {added && <p className="note note-accent mt-4 text-accent">{added}</p>}

      {status.kind === "idle" || status.kind === "loading" ? (
        <p className="mt-4 text-sm text-muted">Loading…</p>
      ) : status.kind === "error" ? (
        <p className="note note-danger mt-4 text-danger">{status.message}</p>
      ) : (
        <ul className="mt-4 flex flex-col gap-2.5">
          {members.map((member) => (
            <AdminRow
              key={member.admin.id}
              member={member}
              assignableRoles={assignableRoles}
              changing={changingId === member.admin.id}
              removing={removingId === member.admin.id}
              onChangeRole={(role) => void actions.changeRole(member.admin, role)}
              onRemove={() => remove(member)}
            />
          ))}
        </ul>
      )}

      <form onSubmit={submit} className="mt-4 border-t border-line pt-4">
        <label htmlFor={emailId} className="text-[13px] font-medium text-white">
          Add someone
        </label>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <input
            id={emailId}
            type="email"
            autoComplete="off"
            value={email}
            onChange={(event) => actions.setEmail(event.target.value)}
            placeholder="their@email.com"
            className="field min-w-56 flex-1"
          />
          {showRolePicker && (
            <select
              id={roleId}
              value={newRole}
              onChange={(event) => actions.setNewRole(event.target.value as StoreRole)}
              className="field btn-sm"
            >
              {assignableRoles.map((role) => (
                <option key={role} value={role}>
                  {roleLabel(role)}
                </option>
              ))}
            </select>
          )}
          <button type="submit" disabled={adding || !email.trim()} className="btn-primary btn-sm">
            {adding ? "Adding…" : "Add to team"}
          </button>
        </div>
        {!showRolePicker && assignableRoles.length > 0 && (
          <p className="mt-2 text-xs leading-snug text-muted">As a Manager you can add and remove Staff.</p>
        )}
      </form>
    </section>
  );
}
